/**
 * Clerk equivalent of `FunnelAuthShell`.
 *
 * Mounts `ClerkProvider` when the resolved application's `authProvider` is
 * `"clerk"` and a `clerkPublishableKey` is configured. For non-Clerk apps
 * (or when the key is missing), children render directly.
 *
 * The provider is headless — no Clerk UI components are mounted. The funnel
 * uses Clerk's `useSignUp()` / `useSession()` hooks exclusively.
 *
 * During loading (or when context is not ready), children render directly
 * so pages like AuthCallbackPage can still mount and complete PKCE exchange.
 */
import { ClerkProvider } from "@clerk/clerk-react";
import { type ReactNode } from "react";
import {
  useApplicationContext,
  type ApplicationContextState,
} from "@src/funnel/useApplicationContext";

export type ClerkAuthShellProps = {
  children: ReactNode;
  contextOverride?: ApplicationContextState;
};

export const ClerkAuthShell = ({ children, contextOverride }: ClerkAuthShellProps) => {
  const live = useApplicationContext();
  const context = contextOverride ?? live;

  if (
    context.status === "ready" &&
    context.application.authProvider === "clerk" &&
    context.application.clerkPublishableKey
  ) {
    return (
      <ClerkProvider publishableKey={context.application.clerkPublishableKey}>
        {children}
      </ClerkProvider>
    );
  }

  return <>{children}</>;
};

export default ClerkAuthShell;
